import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { readString, writeString } from '../lib/storage';

const PreferencesContext = createContext(null);

export const TEXT_SIZES = { sm: 15, md: 16, lg: 17, xl: 18 };

const KEYS = {
  theme: 'lsearch-theme',
  textSize: 'lsearch-text-size',
  density: 'lsearch-density',
  highlightTerms: 'lsearch-highlight-terms',
  highlightSamples: 'lsearch-highlight-samples',
};

export const DEFAULT_PREFERENCES = {
  theme: 'system',
  textSize: 'md',
  density: 'comfortable',
  highlightTerms: true,
  highlightSamples: false,
};

const readInitial = () => {
  const theme = readString(KEYS.theme, DEFAULT_PREFERENCES.theme);

  // Older versions stored a pixel size ("lsearch-fontsize") and three densities.
  let textSize = readString(KEYS.textSize);
  if (!TEXT_SIZES[textSize]) {
    const legacyPx = parseInt(readString('lsearch-fontsize', ''), 10);
    textSize = legacyPx
      ? Object.keys(TEXT_SIZES).reduce((best, k) => (
        Math.abs(TEXT_SIZES[k] - legacyPx) < Math.abs(TEXT_SIZES[best] - legacyPx) ? k : best), 'md')
      : DEFAULT_PREFERENCES.textSize;
  }
  const density = readString(KEYS.density, DEFAULT_PREFERENCES.density);

  return {
    theme: ['system', 'light', 'dark'].includes(theme) ? theme : 'system',
    textSize,
    density: density === 'compact' || density === 'super-compact' ? 'compact' : 'comfortable',
    highlightTerms: readString(KEYS.highlightTerms, 'true') === 'true',
    highlightSamples: readString(KEYS.highlightSamples, 'false') === 'true',
  };
};

const systemTheme = () =>
  (window.matchMedia && window.matchMedia('(prefers-color-scheme: light)').matches ? 'light' : 'dark');

/** Display preferences, persisted per browser and applied to <html>. */
export const PreferencesProvider = ({ children }) => {
  const [prefs, setPrefs] = useState(readInitial);
  const [system, setSystem] = useState(systemTheme);

  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const media = window.matchMedia('(prefers-color-scheme: light)');
    const onChange = () => setSystem(media.matches ? 'light' : 'dark');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  const resolvedTheme = prefs.theme === 'system' ? system : prefs.theme;

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', resolvedTheme);
    root.setAttribute('data-density', prefs.density);
    root.style.fontSize = `${(TEXT_SIZES[prefs.textSize] / 16) * 100}%`;
  }, [resolvedTheme, prefs.density, prefs.textSize]);

  const setPreference = useCallback((key, value) => {
    setPrefs((prev) => ({ ...prev, [key]: value }));
    writeString(KEYS[key], typeof value === 'boolean' ? String(value) : value);
  }, []);

  const resetPreferences = useCallback(() => {
    setPrefs(DEFAULT_PREFERENCES);
    Object.entries(DEFAULT_PREFERENCES).forEach(([key, value]) => writeString(KEYS[key], String(value)));
  }, []);

  const value = useMemo(
    () => ({ ...prefs, resolvedTheme, setPreference, resetPreferences }),
    [prefs, resolvedTheme, setPreference, resetPreferences],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
};

export const usePreferences = () => useContext(PreferencesContext);
